import { h } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { currentStreak } from "@/services/streak.service";

/**
 * Bausteine, die sich Lernpfad, Quiz und Karteikarten teilen: Vollbild-Wurzel,
 * Kopfzeile mit zweifarbiger Fortschrittsleiste und Streak, Fußleiste.
 *
 * Vollbild ist Absicht – wer übt, soll die App-Navigation nicht sehen. Deshalb
 * hängt `.pf` mit `position: fixed` über allem und bringt eigenes Scrolling mit.
 */

export function pfRoot(...children: (Node | null)[]): HTMLElement {
  return h("div", { class: "pf" }, ...children);
}

export interface PfHead {
  el: HTMLElement;
  /** Fortschritt setzen: erledigt / falsch / gesamt. */
  set(done: number, wrong: number, total: number): void;
  /** Rechts oben etwas anderes als den Streak zeigen (z. B. Timer). */
  right: HTMLElement;
}

export function pfHead(onClose: () => void, opts: { streak?: boolean } = {}): PfHead {
  const fillGood = h("i");
  const fillBad = h("b");
  const bar = h("div", { class: "pf-bar" }, fillGood, fillBad);

  const streak = currentStreak();
  const right = h("div", { class: "pf-head-right" });
  if (opts.streak !== false) {
    right.appendChild(
      h(
        "span",
        { class: "pf-streak" + (streak ? "" : " off"), title: "Lern-Streak" },
        h("span", { html: icon("zap", 20) }),
        String(streak)
      )
    );
  }

  const el = h(
    "div",
    { class: "pf-top" },
    h("button", { class: "pf-x", type: "button", title: "Beenden", onclick: onClose }, "✕"),
    bar,
    right
  );

  return {
    el,
    right,
    set(done, wrong, total) {
      const t = Math.max(1, total);
      const goodPct = ((done - wrong) / t) * 100;
      const badPct = (wrong / t) * 100;
      fillGood.style.width = `${Math.max(0, goodPct)}%`;
      fillBad.style.left = `${Math.max(0, goodPct)}%`;
      fillBad.style.width = `${Math.max(0, badPct)}%`;
    },
  };
}

export function pfStage(...children: (Node | null)[]): HTMLElement {
  return h("div", { class: "pf-stage" }, h("div", { class: "pf-body" }, ...children));
}

export function pfLoading(title: string, note = ""): HTMLElement {
  return h(
    "div",
    { class: "pf-loading" },
    h("div", { class: "ring" }),
    h("h3", null, title),
    note ? h("p", null, note) : null
  );
}

export function pfButton(
  label: string,
  onClick: () => void,
  variant: "green" | "blue" | "red" | "gold" | "gray" | "ghost" = "green"
): HTMLButtonElement {
  const cls = variant === "green" ? "" : ` ${variant}`;
  return h("button", { class: `pf-btn${cls}`, type: "button", onclick: onClick }, label) as HTMLButtonElement;
}

export function pfIconButton(
  label: string,
  ic: IconName,
  onClick: () => void,
  variant: "green" | "blue" | "gray" | "ghost" = "green"
): HTMLButtonElement {
  const cls = variant === "green" ? "" : ` ${variant}`;
  return h(
    "button",
    { class: `pf-btn${cls}`, type: "button", onclick: onClick },
    h("span", { class: "row", html: icon(ic, 18) }),
    label
  ) as HTMLButtonElement;
}

export function pfBig(
  label: string,
  onClick: () => void,
  variant: "green" | "blue" | "gray" = "green"
): HTMLButtonElement {
  const b = pfButton(label, onClick, variant);
  b.classList.add("block");
  return b;
}

const enterHandlers = new WeakMap<HTMLElement, (e: KeyboardEvent) => void>();

export function onEnter(root: HTMLElement, fn: () => void): void {
  const prev = enterHandlers.get(root);
  if (prev) document.removeEventListener("keydown", prev);
  const h = (e: KeyboardEvent) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    if (!root.isConnected) return document.removeEventListener("keydown", h);
    e.preventDefault();
    fn();
  };
  enterHandlers.set(root, h);
  document.addEventListener("keydown", h);
}

/** Leerer Zustand mit Aufruf zur Tat. */
export function pfEmpty(title: string, text: string, action?: HTMLElement): HTMLElement {
  return h("div", { class: "pf-empty" }, h("h3", null, title), h("p", null, text), action ?? null);
}
